import ExamMixin from '@/shared/mixins/exam.mixin'
import CONSTANTS from '@/shared/constants/constants'
import FileInfo from '@/shared/mixins/file-info.mixin'
import ExamInformationMixin from '@/shared/mixins/exam-information.mixin'
import ExamFileMixin from '@/shared/mixins/exam-file.mixin'
import { canAttachFileToExam } from '@/shared/utils/exam-attachment'

const ATTACHMENT_TYPE_TRANSLATION_KEYS = {
  Anamnese: 'anamnesis',
  ExameAnterior: 'previous_exam',
  LaudoAnterior: 'previous_report',
  Outros: 'others',
  PedidoMedico: 'medical_request'
}

export default {
  name: 'exam-details-file-upload',
  props: {
    details: {
      type: Object,
      required: true
    }
  },
  mixins: [
    ExamMixin,
    FileInfo,
    ExamInformationMixin,
    ExamFileMixin
  ],
  data () {
    return {
      attachment: '',
      attachmentTypes: [],
      isAttachmentModalOpen: false,
      isLoadingAttachmentTypes: false,
      isUploadingAttachments: false,
      pendingFiles: [],
      STATUS: CONSTANTS.STATUS,
      fileList: []
    }
  },
  computed: {
    examFiles () {
      const examFiles = this.details.examFiles
      if (!examFiles || !examFiles.length) {
        return []
      }

      return examFiles.map((file, index) => {
        let title = `${this.$i18n.t('document')} ${(index + 1)}`
        let extension = file.extension
        if (extension) {
          extension = ` (${extension})`
          title += extension
        }

        return title
      })
    },
    canSubmitAttachments () {
      return this.pendingFiles.length > 0 &&
        this.pendingFiles.every(file => file.attachmentType != null) &&
        !this.isUploadingAttachments
    }
  },
  mounted () {
    this.loadAttachmentTypes()
  },
  methods: {
    showAttachFile () {
      const allowedStatuses = [
        this.STATUS.AGUARDANDO_INFORMACAO,
        this.STATUS.AGUARDANDO_OBSERVACAO,
        this.STATUS.AGUARDANDO_LAUDO,
        this.STATUS.LAUDANDO,
        this.STATUS.AGUARDANDO_RETIFICACAO,
        this.STATUS.ANALISANDO
      ]

      return canAttachFileToExam(
        this.details.status.id,
        allowedStatuses,
        this.STATUS.LAUDO_LIBERADO,
        this.details.allowSendFileAfterReportedExam
      )
    },
    getAttachmentTypeName (attachmentTypeId) {
      const attachmentType = this.attachmentTypes.find(type => type.id === attachmentTypeId)
      return attachmentType
        ? this.getAttachmentTypeLabel(attachmentType)
        : this.$i18n.t('exam_attachment.unclassified')
    },
    getAttachmentTypeLabel (attachmentType) {
      const translationKey = ATTACHMENT_TYPE_TRANSLATION_KEYS[attachmentType.name]
      return translationKey
        ? this.$i18n.t(`exam_attachment.types.${translationKey}`)
        : attachmentType.description
    },
    async openAttachmentModal () {
      this.pendingFiles = []
      this.attachment = ''
      this.isAttachmentModalOpen = true
      await this.loadAttachmentTypes(true)
    },
    async loadAttachmentTypes (showError = false) {

      if (this.attachmentTypes.length || this.isLoadingAttachmentTypes) {
        return
      }

      this.isLoadingAttachmentTypes = true
      try {
        this.attachmentTypes = await this.getAttachmentTypes()
      } catch (error) {
        if (showError) {
          this.$root.$emit('toast', {
            message: this.$i18n.t('exam_attachment.load_types_error'),
            type: 'error'
          })
        }
      } finally {
        this.isLoadingAttachmentTypes = false
      }
    },
    closeAttachmentModal () {
      if (this.isUploadingAttachments) {
        return
      }
      this.isAttachmentModalOpen = false
      this.pendingFiles = []
      this.attachment = ''
    },
    triggerFileInput () {
      this.$refs.attachmentInput.click()
    },
    onSelectAttachment (event) {
      const file = event.target.files?.[0]
      if (!file) {
        return
      }

      this.pendingFiles = [{
        attachmentType: null,
        file,
        lastModified: file.lastModified,
        name: file.name,
        size: file.size
      }]
      event.target.value = ''
      this.attachment = ''
    },
    removePendingFile (index) {
      this.pendingFiles.splice(index, 1)
    },
    async uploadAttachments () {
      if (!this.canSubmitAttachments) {
        return
      }

      this.fileList = this.details.examFiles || []
      this.isUploadingAttachments = true
      this.$root.$emit('showLoading')

      try {
        for (const pendingFile of this.pendingFiles) {
          const fileInfo = await this.getProperties(pendingFile.file)
          const signedFile = await this.getSignedUrl(
            this.details.examId,
            fileInfo.md5,
            fileInfo.extension,
            fileInfo.binaryFileType
          )
          const extension = fileInfo.extension.toLocaleLowerCase()

          await this.uploadFile(pendingFile.file, signedFile.data.linkStorage)
          await this.SetSuccessUploadStatus(signedFile.data.fileId, pendingFile.attachmentType)

          this.fileList.push({
            attachmentType: pendingFile.attachmentType,
            examFileId: signedFile.data.fileId,
            extension
          })
        }

        if (!this.details.examFiles) {
          this.$set(this.details, 'examFiles', this.fileList)
        }
        this.isAttachmentModalOpen = false
        this.pendingFiles = []
        this.attachment = ''
        this.$root.$emit('toast', {
          message: this.$i18n.t('exam_attachment.upload_success'),
          type: 'success'
        })
      } catch (error) {
        this.$root.$emit('toast', {
          message: this.$i18n.t('error.fail_to_send_file'),
          type: 'error'
        })
      } finally {
        this.isUploadingAttachments = false
        this.$root.$emit('hideLoading')
      }
    },
    async showFile (index) {
      const file = this.details.examFiles[index]
      const a = document.createElement('a')
      document.body.appendChild(a)
      a.style = 'display: none'

      if (file.pixeonUid) {
        a.href = import.meta.env.VITE_URL_PIXEON + file.pixeonUid
        a.click()
      } else if (file.extension && file.extension.includes('pdf')) {
        const route = this.$router.resolve({ name: 'examesPdf', query: { id: file.examFileId, isLaudo: 'false' } })
        window.open(route.href, '_blank')
      } else {
        this.$root.$emit('showLoading')
        
        a.href = await this.getExamFileById(file.examFileId, 'octet/stream')
        
        let extension = file.extension
        
        if (!extension || extension === '' || extension === null || extension === undefined) {
          const displayTitle = this.examFiles[index]
          if (displayTitle) {
            const match = displayTitle.match(/\(([^)]+)\)$/)
            if (match && match[1]) {
              extension = match[1].toLowerCase().trim()
            }
          }
        }
        
        if (!extension) {
          extension = 'jpg'
        }
        
        try {
          const url = new URL(a.href)
          const pathname = url.pathname
          const filename = pathname.split('/').pop()
          
          if (filename && filename !== 'File' && !filename.includes('?')) {
            const baseFileName = filename.includes('.') ? filename.replace(/\.[^/.]+$/, '') : filename
            a.download = `${baseFileName}.${extension}`
          } else {
            a.download = `file.${extension}`
          }
        } catch (error) {
          a.download = `file.${extension}`
        }
        
        a.click()
        document.body.removeChild(a)
        this.$root.$emit('hideLoading')
      }
    },
    async downloadFile (index) {
      const file = this.details.examFiles[index]
      try {
        this.$root.$emit('showLoading')
        const href = await this.getExamFileById(file.examFileId, 'octet/stream')
        const link = document.createElement('a')
        link.href = href
        link.download = `${this.$i18n.t('document')} ${index + 1}.${file.extension || 'jpg'}`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        this.$root.$emit('toast', {
          message: this.$i18n.t('error.unexpected_error_occurred'),
          type: 'error'
        })
      } finally {
        this.$root.$emit('hideLoading')
      }
    }
  }
}
